import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { MdChevronLeft, MdChevronRight, MdOutlineReceiptLong, MdRefresh } from 'react-icons/md';
import { fetchAuthenticatedImage } from '../../../api/services/authService';
import { getMyBnbBookings } from '../../../api/services/propertyService';
import { getRentalStatus, getTenantRentEvidence } from '../../../api/services/rentalService';
import { BROKEN_IMAGE_PLACEHOLDER_SRC, getImageFromCache, storeImageInCache } from '../../../utils/imageCache';
import { isRemoteHttpUrl, openAuthenticatedImageInNewTab } from '../../../utils/openAuthenticatedImage';
import PayRentEvidenceModal from './PayRentEvidenceModal';
import CollapsibleTopPanel from '../../../components/CollapsibleTopPanel/CollapsibleTopPanel';
import './PaymentHistory.css';

const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

const TYPE_FILTER_OPTIONS = [
  { value: 'all', label: 'All payments' },
  { value: 'rental', label: 'Rentals' },
  { value: 'bnb', label: 'BnB bookings' },
];

const formatMoneyKes = (value) => {
  const num = Number(value);
  if (!Number.isFinite(num)) return '—';
  const hasCents = Math.floor(num) !== num;
  const formatted = num.toLocaleString(undefined, {
    minimumFractionDigits: hasCents ? 2 : 0,
    maximumFractionDigits: 2,
  });
  return `KES ${formatted}`;
};

const formatDateTime = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return String(iso);
  return d.toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const formatShortDate = (iso) => {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
};

const periodLabel = (row) => {
  if (row.paymentType === 'bnb') {
    const checkIn = formatShortDate(row.checkInDate);
    const checkOut = formatShortDate(row.checkOutDate);
    if (checkIn && checkOut) return `${checkIn} – ${checkOut}`;
    if (checkIn) return checkIn;
    return '—';
  }
  if (row.periodYear != null && row.periodMonth != null) {
    return `${MONTH_NAMES[row.periodMonth - 1] || row.periodMonth} ${row.periodYear}`;
  }
  return '—';
};

const getRowFilterMonthYear = (row) => {
  if (row.paymentType === 'bnb') {
    const d = new Date(row.createdAt || row.checkInDate || 0);
    if (Number.isNaN(d.getTime())) return { year: null, month: null };
    return { year: d.getFullYear(), month: d.getMonth() + 1 };
  }
  return { year: row.periodYear, month: row.periodMonth };
};

function getTenantAssignmentId(rental) {
  if (!rental || typeof rental !== 'object') return null;
  return (
    rental.assignmentId ??
    rental.tenantAssignmentId ??
    rental.unitAssignmentId ??
    rental.assignment?.id ??
    null
  );
}

function evidenceStatusClass(status) {
  const s = String(status || '').toLowerCase();
  if (s === 'approved') return 'evidence-status-approved';
  if (s === 'rejected') return 'evidence-status-rejected';
  if (s === 'pending') return 'evidence-status-pending';
  return 'evidence-status-other';
}

function evidenceStatusLabel(status) {
  const s = String(status || '').toUpperCase();
  if (s === 'APPROVED') return 'Approved';
  if (s === 'REJECTED') return 'Rejected';
  if (s === 'PENDING') return 'Pending';
  return s.replace(/_/g, ' ') || '—';
}

/**
 * Latest rejected evidence for the selected calendar month (for resubmit UI).
 */
function getRentEvidenceResubmitFromList(rental, year, month, evidenceRows) {
  const assignmentId = getTenantAssignmentId(rental);
  if (!assignmentId) {
    return { show: false, assignmentId: null, rejectionReason: '', prefill: {} };
  }

  const rejected = evidenceRows.filter(
    (e) =>
      String(e.status || '').toUpperCase() === 'REJECTED' &&
      e.assignmentId === assignmentId &&
      Number(e.periodYear) === Number(year) &&
      Number(e.periodMonth) === Number(month)
  );

  if (rejected.length === 0) {
    return { show: false, assignmentId, rejectionReason: '', prefill: {} };
  }

  rejected.sort((a, b) => {
    const ta = new Date(a.reviewedAt || a.createdAt || 0).getTime();
    const tb = new Date(b.reviewedAt || b.createdAt || 0).getTime();
    return tb - ta;
  });

  const latest = rejected[0];
  return {
    show: true,
    assignmentId,
    rejectionReason: latest.rejectionReason || '',
    prefill: {
      amountPaid: latest.amountPaid != null ? String(latest.amountPaid) : '',
      mpesaMessage: typeof latest.mpesaMessage === 'string' ? latest.mpesaMessage : '',
      photoUrl: typeof latest.photoUrl === 'string' ? latest.photoUrl : '',
    },
  };
}

function parseRentEvidenceResponse(res) {
  const raw = res?.data;
  if (Array.isArray(raw)) return raw;
  return [];
}

function parseBnbBookingsResponse(res) {
  const payload = res?.data ?? res;
  const list = Array.isArray(payload) ? payload : payload?.data ?? [];
  return Array.isArray(list) ? list : [];
}

function bookingHasPaymentEvidence(booking) {
  if (!booking || typeof booking !== 'object') return false;
  return Boolean(
    booking.depositEvidenceText ||
      booking.depositEvidenceUrl ||
      booking.bookingFeeEvidenceText ||
      booking.bookingFeeEvidencePhotoUrl ||
      booking.firstMonthEvidenceText ||
      booking.firstMonthEvidenceUrl ||
      booking.depositPaidAmount ||
      booking.firstMonthPaidAmount
  );
}

function shouldIncludeBnbPayment(booking) {
  const status = String(booking?.status || '').toUpperCase();
  if (status === 'CANCELLED') return false;
  if (status === 'CONFIRMED') return true;
  return bookingHasPaymentEvidence(booking);
}

function mapBnbPaymentStatus(booking) {
  const status = String(booking?.status || '').toUpperCase();
  if (status === 'CONFIRMED' || booking?.depositConfirmed) return 'APPROVED';
  if (status === 'REJECTED') return 'REJECTED';
  if (bookingHasPaymentEvidence(booking)) return 'PENDING';
  return 'PENDING';
}

function normalizeRentalEvidenceRow(row) {
  return {
    ...row,
    paymentType: 'rental',
  };
}

function normalizeBnbBookingRow(booking) {
  const amountPaid =
    Number(booking.depositPaidAmount) ||
    Number(booking.firstMonthPaidAmount) ||
    Number(booking.totalAmount) ||
    0;
  const createdAt = booking.createdAt || booking.checkInDate || null;
  const createdDate = createdAt ? new Date(createdAt) : null;
  const filterYear = createdDate && !Number.isNaN(createdDate.getTime()) ? createdDate.getFullYear() : null;
  const filterMonth = createdDate && !Number.isNaN(createdDate.getTime()) ? createdDate.getMonth() + 1 : null;

  return {
    id: booking.id || booking.bookingId,
    paymentType: 'bnb',
    amountPaid,
    status: mapBnbPaymentStatus(booking),
    createdAt,
    periodYear: filterYear,
    periodMonth: filterMonth,
    propertyName: booking.propertyName,
    unitName: booking.unitName || booking.unitNumber,
    reviewedAt: booking.confirmedAt || (String(booking.status || '').toUpperCase() === 'CONFIRMED' ? booking.updatedAt : null),
    reviewedByName: booking.confirmedByName || booking.reviewedByName || null,
    rejectionReason: booking.rejectionReason || booking.cancelReason || null,
    photoUrl:
      booking.depositEvidenceUrl ||
      booking.bookingFeeEvidencePhotoUrl ||
      booking.firstMonthEvidenceUrl ||
      '',
    mpesaMessage:
      booking.depositEvidenceText ||
      booking.bookingFeeEvidenceText ||
      booking.firstMonthEvidenceText ||
      '',
    checkInDate: booking.checkInDate,
    checkOutDate: booking.checkOutDate,
    bookingCode: booking.bookingCode,
    bookingStatus: booking.status,
  };
}

function RentEvidencePhoto({ photoUrl }) {
  const [displaySrc, setDisplaySrc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const [openError, setOpenError] = useState('');

  useEffect(() => {
    if (!photoUrl) return undefined;
    if (!isRemoteHttpUrl(photoUrl)) {
      setDisplaySrc(photoUrl);
      setLoading(false);
      setFailed(false);
      return undefined;
    }
    let cancelled = false;
    (async () => {
      setLoading(true);
      setFailed(false);
      try {
        const cached = getImageFromCache(photoUrl);
        if (cached) {
          if (!cancelled) {
            setDisplaySrc(cached);
            setLoading(false);
          }
          return;
        }
        const blob = await fetchAuthenticatedImage(photoUrl);
        if (!cancelled) {
          if (blob) {
            await storeImageInCache(photoUrl, blob);
            setDisplaySrc(blob);
          } else {
            setFailed(true);
          }
          setLoading(false);
        }
      } catch {
        if (!cancelled) {
          setFailed(true);
          setLoading(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [photoUrl]);

  const handleOpenFull = async (e) => {
    e.preventDefault();
    setOpenError('');
    try {
      await openAuthenticatedImageInNewTab(photoUrl);
    } catch (err) {
      setOpenError(err?.message || 'Could not open image.');
    }
  };

  if (!photoUrl) return null;

  return (
    <div className="evidence-detail-row">
      <span className="payment-notes-label">Photo</span>
      <div className="evidence-photo-wrap">
        {loading ? <p className="payment-notes-text evidence-photo-status">Loading preview…</p> : null}
        {!loading && failed ? (
          <p className="payment-notes-text evidence-photo-status evidence-photo-status--error">
            Preview unavailable. Open full image may still work.
          </p>
        ) : null}
        {!loading && displaySrc && !failed ? (
          <img
            src={displaySrc}
            alt="Rent payment evidence"
            className="evidence-photo-thumb"
            onError={(ev) => {
              ev.currentTarget.onerror = null;
              ev.currentTarget.src = BROKEN_IMAGE_PLACEHOLDER_SRC;
            }}
          />
        ) : null}
        <p className="payment-notes-text">
          <button type="button" className="evidence-photo-open-btn" onClick={handleOpenFull}>
            View full image
          </button>
        </p>
        {openError ? (
          <p className="payment-notes-text evidence-photo-status--error" role="alert">
            {openError}
          </p>
        ) : null}
      </div>
    </div>
  );
}

const PaymentHistory = () => {
  const now = useMemo(() => new Date(), []);
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [typeFilter, setTypeFilter] = useState('all');
  const [rentalEvidenceRows, setRentalEvidenceRows] = useState([]);
  const [bnbPaymentRows, setBnbPaymentRows] = useState([]);
  const [rentalForEvidence, setRentalForEvidence] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [resubmitModalOpen, setResubmitModalOpen] = useState(false);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const [evRes, rentalRes, bnbRes] = await Promise.all([
        getTenantRentEvidence(),
        getRentalStatus().catch(() => null),
        getMyBnbBookings().catch(() => null),
      ]);

      const rentalList = parseRentEvidenceResponse(evRes).map(normalizeRentalEvidenceRow);
      const sortedRental = [...rentalList].sort((a, b) => {
        const ta = new Date(a.createdAt || 0).getTime();
        const tb = new Date(b.createdAt || 0).getTime();
        return tb - ta;
      });
      setRentalEvidenceRows(sortedRental);

      const bnbList = bnbRes ? parseBnbBookingsResponse(bnbRes) : [];
      const bnbPayments = bnbList
        .filter(shouldIncludeBnbPayment)
        .map(normalizeBnbBookingRow)
        .sort((a, b) => new Date(b.createdAt || 0).getTime() - new Date(a.createdAt || 0).getTime());
      setBnbPaymentRows(bnbPayments);

      const rentalData =
        rentalRes?.status === 'success' && rentalRes?.data ? rentalRes.data : null;
      setRentalForEvidence(rentalData);
    } catch (e) {
      setRentalEvidenceRows([]);
      setBnbPaymentRows([]);
      setRentalForEvidence(null);
      setError(e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    setResubmitModalOpen(false);
  }, [year, month]);

  const allPaymentRows = useMemo(
    () => [...rentalEvidenceRows, ...bnbPaymentRows],
    [rentalEvidenceRows, bnbPaymentRows]
  );

  const filteredRows = useMemo(() => {
    return allPaymentRows.filter((row) => {
      const typeOk =
        typeFilter === 'all' ||
        (typeFilter === 'rental' && row.paymentType === 'rental') ||
        (typeFilter === 'bnb' && row.paymentType === 'bnb');
      if (!typeOk) return false;

      const { year: rowYear, month: rowMonth } = getRowFilterMonthYear(row);
      return Number(rowYear) === Number(year) && Number(rowMonth) === Number(month);
    });
  }, [allPaymentRows, typeFilter, year, month]);

  const rentEvidenceResubmit = useMemo(
    () => {
      if (typeFilter === 'bnb') {
        return { show: false, assignmentId: null, rejectionReason: '', prefill: {} };
      }
      return getRentEvidenceResubmitFromList(rentalForEvidence, year, month, rentalEvidenceRows);
    },
    [rentalForEvidence, year, month, rentalEvidenceRows, typeFilter]
  );

  const rejectedLeaseEvidenceForModal = useMemo(() => {
    if (!rentEvidenceResubmit.show) return undefined;
    return {
      periodYear: year,
      periodMonth: month,
      rejectionReason: rentEvidenceResubmit.rejectionReason,
      prefill: rentEvidenceResubmit.prefill,
    };
  }, [
    rentEvidenceResubmit.show,
    year,
    month,
    rentEvidenceResubmit.rejectionReason,
    rentEvidenceResubmit.prefill,
  ]);

  const shiftMonth = (delta) => {
    let m = month + delta;
    let y = year;
    while (m < 1) {
      m += 12;
      y -= 1;
    }
    while (m > 12) {
      m -= 12;
      y += 1;
    }
    setMonth(m);
    setYear(y);
  };

  const monthTotal = useMemo(
    () => filteredRows.reduce((sum, r) => sum + (Number(r.amountPaid) || 0), 0),
    [filteredRows]
  );

  const yearOptions = useMemo(() => {
    const current = now.getFullYear();
    const list = [];
    for (let y = current + 1; y >= current - 5; y -= 1) {
      list.push(y);
    }
    return list;
  }, [now]);

  return (
    <div className="payment-history-page">
      <CollapsibleTopPanel id="tenant-payment-history-top" defaultOpenOnMobile={false}>
        <header className="payment-history-header">
          <div className="payment-history-title-block">
            <h1 className="payment-history-title">Payments</h1>
            <p className="payment-history-subtitle">
              Monthly rent evidence and BnB booking payments in one place
            </p>
          </div>
          <button
            type="button"
            className="payment-history-refresh"
            onClick={load}
            disabled={loading}
            aria-label="Refresh"
          >
            <MdRefresh className={loading ? 'spin' : ''} />
          </button>
        </header>

        <section className="payment-history-toolbar" aria-label="Filter payments">
          <div className="payment-history-filters">
            <div className="payment-history-filter-group">
              <label className="payment-history-filter-label" htmlFor="ph-type">
                Type
              </label>
              <select
                id="ph-type"
                className="payment-history-filter-select"
                value={typeFilter}
                onChange={(e) => setTypeFilter(e.target.value)}
                disabled={loading}
              >
                {TYPE_FILTER_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <div className="payment-history-toolbar-main">
            <div className="period-nav">
              <button type="button" className="period-arrow" onClick={() => shiftMonth(-1)} aria-label="Previous month">
                <MdChevronLeft />
              </button>
              <div className="period-selects">
                <label className="visually-hidden" htmlFor="ph-month">Month</label>
                <select
                  id="ph-month"
                  className="period-select"
                  value={month}
                  onChange={(e) => setMonth(Number(e.target.value))}
                >
                  {MONTH_NAMES.map((name, i) => (
                    <option key={name} value={i + 1}>
                      {name}
                    </option>
                  ))}
                </select>
                <label className="visually-hidden" htmlFor="ph-year">Year</label>
                <select
                  id="ph-year"
                  className="period-select"
                  value={year}
                  onChange={(e) => setYear(Number(e.target.value))}
                >
                  {yearOptions.map((y) => (
                    <option key={y} value={y}>
                      {y}
                    </option>
                  ))}
                </select>
              </div>
              <button type="button" className="period-arrow" onClick={() => shiftMonth(1)} aria-label="Next month">
                <MdChevronRight />
              </button>
            </div>
            <div className="period-summary-card">
              <div className="period-summary-label">Total claimed this period</div>
              <div className="period-summary-value">{formatMoneyKes(monthTotal)}</div>
              <div className="period-summary-meta">
                {filteredRows.length} submission{filteredRows.length === 1 ? '' : 's'}
              </div>
            </div>
          </div>
        </section>
      </CollapsibleTopPanel>

      {!loading && !error && rentEvidenceResubmit.show && (
        <section className="ph-resubmit-cta" aria-labelledby="ph-resubmit-cta-title">
          <div className="ph-resubmit-cta-card">
            <div className="ph-resubmit-cta-text">
              <h2 id="ph-resubmit-cta-title" className="ph-resubmit-cta-title">
                Resubmission needed
              </h2>
              <p className="ph-resubmit-cta-intro">
                Your proof for{' '}
                <strong>
                  {MONTH_NAMES[month - 1]} {year}
                </strong>{' '}
                was rejected. Resubmit and send an updated M-Pesa SMS or a receipt photo.
              </p>
              {rentEvidenceResubmit.rejectionReason ? (
                <p className="ph-resubmit-cta-reason">
                  <span className="ph-resubmit-cta-reason-label">Reason</span>
                  {rentEvidenceResubmit.rejectionReason}
                </p>
              ) : null}
            </div>
            <button
              type="button"
              className="ph-resubmit-cta-button"
              onClick={() => setResubmitModalOpen(true)}
            >
              Submit proof
            </button>
          </div>
        </section>
      )}

      <PayRentEvidenceModal
        open={resubmitModalOpen}
        onClose={() => setResubmitModalOpen(false)}
        rental={rentalForEvidence}
        rejectedLeaseEvidence={rejectedLeaseEvidenceForModal}
        hideSuccessPaymentsNav
        onSubmitted={() => {
          load();
        }}
      />

      {loading && (
        <div className="payment-history-state">
          <div className="skeleton-line wide" />
          <div className="skeleton-line" />
          <div className="skeleton-line" />
        </div>
      )}

      {!loading && error && (
        <div className="payment-history-error" role="alert">
          <div className="payment-history-error-title">Could not load payments</div>
          <p className="payment-history-error-msg">{error.message || 'Something went wrong.'}</p>
          <button type="button" className="payment-history-retry" onClick={load}>
            Try again
          </button>
        </div>
      )}

      {!loading && !error && filteredRows.length === 0 && !rentEvidenceResubmit.show && (
        <div className="payment-history-empty">
          <div className="payment-history-empty-icon">
            <MdOutlineReceiptLong />
          </div>
          <div className="payment-history-empty-title">No payments for this month</div>
          <p className="payment-history-empty-text">
            {typeFilter === 'bnb' ? (
              <>
                BnB booking payments submitted in{' '}
                <strong>
                  {MONTH_NAMES[month - 1]} {year}
                </strong>{' '}
                will show here. Book a stay and submit payment proof from your requests.
              </>
            ) : typeFilter === 'rental' ? (
              <>
                Submitted rent proofs for{' '}
                <strong>
                  {MONTH_NAMES[month - 1]} {year}
                </strong>{' '}
                will show here. Use Pay rent on your dashboard to submit proof.
              </>
            ) : (
              <>
                Rental and BnB payments for{' '}
                <strong>
                  {MONTH_NAMES[month - 1]} {year}
                </strong>{' '}
                will show here.
              </>
            )}
          </p>
        </div>
      )}

      {!loading && !error && filteredRows.length > 0 && (
        <div className="payment-history-list evidence-list" role="list">
          <div className="payment-table-head evidence-table-head" aria-hidden="true">
            <span>Type</span>
            <span>Status</span>
            <span>Amount</span>
            <span>Submitted</span>
            <span>Period</span>
            <span>Property</span>
          </div>
          {filteredRows.map((row) => (
            <article
              key={`${row.paymentType}-${row.id}`}
              className="payment-card evidence-card"
              role="listitem"
            >
              <span
                className={`payment-type-chip payment-cell payment-cell-type ${row.paymentType === 'bnb' ? 'payment-type-chip--bnb' : 'payment-type-chip--rental'}`}
              >
                {row.paymentType === 'bnb' ? 'BnB' : 'Rental'}
              </span>
              <span
                className={`payment-type-badge evidence-status-badge payment-cell payment-cell-status ${evidenceStatusClass(row.status)}`}
              >
                {evidenceStatusLabel(row.status)}
              </span>
              <span className="payment-card-amount">{formatMoneyKes(row.amountPaid)}</span>
              <span className="payment-cell payment-cell-paid evidence-cell-submitted">
                {formatDateTime(row.createdAt)}
              </span>
              <span
                className={`payment-cell payment-cell-period ${row.paymentType === 'bnb' ? 'payment-cell-stay' : ''}`}
              >
                {periodLabel(row)}
              </span>
              <span className="payment-cell payment-cell-by evidence-cell-property">
                {[row.propertyName, row.unitName || row.unitNumber].filter(Boolean).join(' · ') || '—'}
              </span>
              <div className="payment-card-notes evidence-card-details">
                {row.paymentType === 'bnb' && row.bookingCode ? (
                  <div className="evidence-detail-row">
                    <span className="payment-notes-label">Booking code</span>
                    <p className="payment-notes-text">{row.bookingCode}</p>
                  </div>
                ) : null}
                {row.reviewedAt ? (
                  <div className="evidence-detail-row">
                    <span className="payment-notes-label">Reviewed</span>
                    <p className="payment-notes-text">
                      {formatDateTime(row.reviewedAt)}
                      {row.reviewedByName ? ` · ${row.reviewedByName}` : ''}
                    </p>
                  </div>
                ) : null}
                {String(row.status || '').toUpperCase() === 'REJECTED' && row.rejectionReason ? (
                  <div className="evidence-detail-row evidence-rejection-box">
                    <span className="payment-notes-label">Rejection reason</span>
                    <p className="payment-notes-text">{row.rejectionReason}</p>
                  </div>
                ) : null}
                {row.photoUrl ? <RentEvidencePhoto photoUrl={row.photoUrl} /> : null}
                {row.mpesaMessage ? (
                  <details className="evidence-mpesa-details">
                    <summary className="evidence-mpesa-summary">M-Pesa message</summary>
                    <pre className="evidence-mpesa-pre">{row.mpesaMessage}</pre>
                  </details>
                ) : null}
              </div>
            </article>
          ))}
        </div>
      )}
    </div>
  );
};

export default PaymentHistory;
